import { Table } from '@getsynapse/design-system';
import classnames from 'classnames';
import { get, isEmpty, isNaN } from 'lodash';
import useOptions from 'Pages/BudgetsPage/hooks/useOptions';
import React, { useEffect, useRef, useState } from 'react';
import { CSVLink } from 'react-csv';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import {
  BUDGET_CSV_EXPORT_CONSTANTS,
  BUDGET_CSV_HEADER_SERIAL,
  BudgetFilters,
} from 'utils/constants/budget';
import { ParentOfLineItems } from 'utils/types/budget';
import { v4 as uuidv4 } from 'uuid';
import useSnackbarNotification from '../../../../../Hooks/useSnackbarNotification';
import {
  deleteLineItem,
  getBudgetViewType,
  getSelectedBudget,
  updateLineItemActual,
  updateLineItemNote,
} from '../../../../../state/BudgetList/budgetListSlice';
import { EDIT_BUDGET_TABS, MONTHS_CAP } from '../../../../../utils/constants';
import {
  ActualTimes,
  UpdateLineItemActualsRequest,
} from '../../../../../utils/constants/budget';
import {
  LineItemWithAmounts,
  Option,
  TimeFrame,
} from '../../../../../utils/customTypes';
import useBudgetLineItemCalculations from '../../../hooks/useBudgetLineItemCalculations';
import { DeleteLineItemModal } from '../../Plan/components/modals/DeleteLineItemModal';
import AttachmentsSidePanel from '../../Plan/components/sidepanels/AttachmentsSidePanel';
import NoteSidePanel from '../../Plan/components/sidepanels/NoteSidePanel';
import {
  calculateLineItemActualCell,
  calculateLineItemAllocationCell,
  monthIndecesForYearToDate,
} from '../helper/reportHelpers';
import { generateMultipleReportRows } from '../helper/reportTableHelpers';
import BudgetReportTableHeader from './BudgetReportTableHeader';
import { formatCurrency } from 'Pages/BudgetsPage/helpers/budgetHelper';

const BudgetReportTable: React.FC<{
  selectedTime: Option;
  sumOfAmounts: number;
  sumOfActuals: number;
  toggleFilterComponent: () => void;
  savedFilters: BudgetFilters;
  handleSaveFiltersSettings: (updatedFilters: BudgetFilters) => void;
}> = ({
  selectedTime,
  sumOfAmounts,
  sumOfActuals,
  toggleFilterComponent,
  savedFilters,
  handleSaveFiltersSettings,
}) => {
  const dispatch = useDispatch();
  const selectedBudget = useSelector(getSelectedBudget);
  const [fiscalYear, setFiscalYear] = useState<number>(
    new Date().getUTCFullYear()
  );
  const [fiscalMonth, setFiscalMonth] = useState<number>(new Date().getMonth());

  const selectedBudgetView = useSelector(getBudgetViewType);
  const [selectedBudgetInfo, setSelectedBudgetInfo] =
    useState<LineItemWithAmounts | null>(null);
  const [displayDeleteModal, setDisplayDeleteModal] = useState(false);
  const [displayAttachmentModal, setDisplayAttachmentModal] = useState(false);
  const [storedLineItem, setStoredLineItem] = useState<string>();

  const {
    vendorOptions,
    learningTeamOptions,
    programOptions,
    projectOptions,
    vendorLookup,
    learningTeamLookup,
    programLookup,
    projectLookup,
  } = useOptions();

  const { parentsHavingLineItems } = useBudgetLineItemCalculations();
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [focusedRows, setFocusedRows] = useState<Record<string, boolean>>({});
  const [checkedRows, setCheckedRows] = useState<Record<string, boolean>>({});
  const [focusedCellId, setFocusedCellId] = useState<string>('');
  const [storedNoteData, setStoredNoteData] =
    useState<LineItemWithAmounts | null>(null);
  const [shouldDisplayNoteSidePanel, setShouldDisplayNoteSidePanel] =
    useState<boolean>(false);

  const csvLinkRef = useRef<any>(null);
  const [exportFileName, setExportFileName] = useState('');
  const [csvHeaders, setCsvHeaders] = useState<
    { label: string; key: string }[]
  >([]);
  const [csvData, setCsvData] = useState<Record<string, any>[]>([]);

  const toggleExpandedRow = (rowId: string) => {
    setExpandedRows((prev) => ({
      ...prev,
      [rowId]: !prev[rowId],
    }));
  };

  const toggleFocusedRow = ({
    rowId,
    isFocused,
    isOnlyChecked = false,
  }: {
    rowId: string;
    isFocused?: boolean;
    isOnlyChecked?: boolean;
  }) => {
    if (!isOnlyChecked) {
      setFocusedRows((prev) => ({
        ...prev,
        [rowId]: isFocused == null ? !prev[rowId] : isFocused,
      }));
    }

    setCheckedRows((prev) => ({
      ...prev,
      [rowId]: isFocused == null ? !prev[rowId] : isFocused,
    }));
  };

  useEffect(() => {
    setFiscalYear(
      selectedBudget && selectedBudget.fiscalPeriodStart
        ? new Date(selectedBudget?.fiscalPeriodStart!).getUTCFullYear()
        : new Date().getFullYear()
    );
    setFiscalMonth(
      selectedBudget && selectedBudget.fiscalPeriodStart
        ? new Date(selectedBudget.fiscalPeriodStart).getUTCMonth()
        : new Date().getUTCMonth()
    );
  }, [selectedBudget]);

  useEffect(() => {
    setExpandedRows({});
    setFocusedCellId('');
    setFocusedRows({});
  }, [selectedBudgetView, selectedBudget]);

  const {
    showNotification,
    isNotificationVisible,
    snackbarProps,
    SnackbarNotification,
  } = useSnackbarNotification();

  function handleAddNote(item: LineItemWithAmounts) {
    setStoredNoteData(item);
    setShouldDisplayNoteSidePanel(true);
  }

  function handleDeleteLineItem(item: LineItemWithAmounts) {
    setSelectedBudgetInfo(item);
    setDisplayDeleteModal(true);
  }

  const toggleNoteSidePanel = () =>
    setShouldDisplayNoteSidePanel((prevState) => !prevState);

  const setItemToDelete = async () => {
    if (selectedBudgetInfo == null) {
      return;
    }

    const removedLineItem = await dispatch(
      deleteLineItem(selectedBudgetInfo!.id)
    );
    if (get(removedLineItem, 'payload.data.id', null)) {
      showNotification({
        notificationVariant: 'success',
        notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
        notificationMessage: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.BUDGET_DELETION_SUCCESS',
          { title: selectedBudgetInfo?.description || 'Budget Item' }
        ),
        hasActionButton: true,
      });
    } else if (get(removedLineItem, 'error', null)) {
      showNotification({
        notificationVariant: 'error',
        notificationTitle: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.ERROR_TITLE'
        ),
        notificationMessage: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.UNEXPECTED_ERROR',
          { title: selectedBudgetInfo?.description }
        ),
        hasActionButton: true,
      });
    }

    setDisplayDeleteModal(false);
  };

  function handleAddAttachment(itemId: string) {
    // to do
    setStoredLineItem(itemId);
    setDisplayAttachmentModal(true);
  }

  const onNoteSaved = async (note: string) => {
    const response = await dispatch(
      updateLineItemNote({
        id: storedNoteData!.id,
        note: note,
      })
    );
    if (get(response, 'payload.data')) {
      setShouldDisplayNoteSidePanel(false);
      showNotification({
        notificationVariant: 'success',
        notificationTitle: intl.get('SUCCESS_SNACKBAR_TITLE'),
        notificationMessage: intl.get(
          'BUDGET_PAGE.NOTIFICATIONS.NOTE_UPDATE_SUCCESS',
          { title: selectedBudgetInfo?.note }
        ),
        hasActionButton: true,
      });
    } else if (get(response, 'error', null)) {
      showNotification({
        notificationVariant: 'error',
        notificationTitle: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.ERROR_TITLE'
        ),
        notificationMessage: intl.get(
          'BUDGET_PAGE.DELETE_BUDGET_MODAL.NOTIFICATIONS.UNEXPECTED_ERROR',
          { title: selectedBudgetInfo?.note }
        ),
        hasActionButton: true,
      });
    }
  };

  async function onEditLineItem(amount: number, lineItemId: string) {
    let updatedMonths: string[] = [];
    const index = parseFloat(selectedTime.value);

    if (
      !isNaN(parseFloat(selectedTime.value)) &&
      isFinite(selectedTime.value)
    ) {
      switch (selectedBudget?.timeFrame) {
        case TimeFrame.MONTHLY:
          updatedMonths.push(MONTHS_CAP[index]);
          break;
        case TimeFrame.QUARTERLY:
          for (let i = 0; i < 3; i++) {
            updatedMonths.push(MONTHS_CAP[(index * 3 + i + fiscalMonth) % 12]);
          }

          break;
        case TimeFrame.YEARLY:
          updatedMonths = [...MONTHS_CAP];
          break;
        default:
          break;
      }
    } else if (selectedTime.value === ActualTimes.FISCAL_CURRENT) {
      updatedMonths = [...MONTHS_CAP];
    }

    let req: UpdateLineItemActualsRequest = {
      budgetId: selectedBudget!.id,
      lineItemId: lineItemId,
      actual: amount,
      months: updatedMonths,
    };

    dispatch(updateLineItemActual(req));
  }

  const exportCsvData = () => {
    const timeFrame = selectedBudget?.timeFrame ?? TimeFrame.QUARTERLY;

    let data: Record<string, any>[] = [];

    const headersKeys =
      BUDGET_CSV_HEADER_SERIAL[
        selectedBudgetView?.value as keyof typeof BUDGET_CSV_HEADER_SERIAL
      ] || [];

    let newHeaders = headersKeys.map((key) => ({
      label: intl.get(`BUDGET_PAGE.EDIT.TABLE.HEAD.${key}`),
      key: BUDGET_CSV_EXPORT_CONSTANTS[key].KEY,
    }));

    const sectionFields = [
      BUDGET_CSV_EXPORT_CONSTANTS.REPORT_SECTION_ALLOCATED,
      BUDGET_CSV_EXPORT_CONSTANTS.REPORT_SECTION_ACTUALS,
      BUDGET_CSV_EXPORT_CONSTANTS.REPORT_SECTION_VARIANCE,
    ].map(({ label, KEY }) => ({
      label: `${selectedTime.label} ${label}`,
      key: KEY,
    }));

    const fiscalYearLabel =
      intl.get('BUDGET_PAGE.FISCAL_YEAR') + ' ' + fiscalYear + ' ';

    const fiscalYearFields = [
      BUDGET_CSV_EXPORT_CONSTANTS.REPORT_FISCAL_YEAR_ALLOCATED,
      BUDGET_CSV_EXPORT_CONSTANTS.REPORT_FISCAL_YEAR_ACTUALS,
      BUDGET_CSV_EXPORT_CONSTANTS.REPORT_FISCAL_YEAR_VARIANCE,
    ].map(({ label, KEY }) => ({
      label: `${fiscalYearLabel}${label}`,
      key: KEY,
    }));

    newHeaders.push(...sectionFields, ...fiscalYearFields);

    newHeaders.push({
      label: intl.get('BUDGET_PAGE.EDIT.TABLE.HEAD.NOTES'),
      key: BUDGET_CSV_EXPORT_CONSTANTS.NOTES.KEY,
    });

    data = generateCsvDataFromParent(
      parentsHavingLineItems,
      fiscalMonth,
      timeFrame
    );

    const formattedDate = new Date().toDateString();
    setExportFileName(
      `budget_report_exports_${
        selectedBudget?.title
      }_${uuidv4()}_${formattedDate}.csv`
    );

    setCsvHeaders(newHeaders);
    setCsvData(data);

    setTimeout(() => {
      if (csvLinkRef.current) {
        csvLinkRef.current.link.click();
      }
    }, 1000);
  };

  function generateCsvDataFromParent(
    parents: ParentOfLineItems[],
    fiscalYearStart: number,
    timeFrame: TimeFrame
  ) {
    let data: Record<string, any>[] = [];

    let monthsForYearToDate = monthIndecesForYearToDate(
      fiscalYear,
      fiscalMonth
    );

    parents.forEach((parent) => {
      parent.lineItems?.forEach((lineItem) => {
        let row: Record<string, any> = {};

        row[BUDGET_CSV_EXPORT_CONSTANTS.DESCRIPTION.KEY] = lineItem.description;
        row[BUDGET_CSV_EXPORT_CONSTANTS.TEAM.KEY] = lineItem.learningTeamId
          ? learningTeamLookup[lineItem.learningTeamId]
          : '';
        row[BUDGET_CSV_EXPORT_CONSTANTS.PROGRAM.KEY] = lineItem.programId
          ? programLookup[lineItem.programId]
          : '';
        row[BUDGET_CSV_EXPORT_CONSTANTS.PROJECT.KEY] = lineItem.projectId
          ? projectLookup[lineItem.projectId]
          : '';
        row[BUDGET_CSV_EXPORT_CONSTANTS.BUDGET_SOURCE.KEY] =
          lineItem.budgetSource;
        row[BUDGET_CSV_EXPORT_CONSTANTS.VENDOR.KEY] = lineItem.vendorId
          ? vendorLookup[lineItem.vendorId]
          : '';

        const allocation = calculateLineItemAllocationCell(
          lineItem,
          selectedTime
        );
        const actual = calculateLineItemActualCell(
          lineItem,
          selectedTime,
          monthsForYearToDate
        );
        const fiscalAmount = lineItem.rawAmounts.reduce(
          (total: number, item: number) => total + (item ?? 0),
          0
        );
        const fiscalActual = lineItem.rawActuals.reduce(
          (total: number, item: number) => total + (item ?? 0),
          0
        );

        row[BUDGET_CSV_EXPORT_CONSTANTS.REPORT_SECTION_ALLOCATED.KEY] =
          formatCurrency(allocation);
        row[BUDGET_CSV_EXPORT_CONSTANTS.REPORT_SECTION_ACTUALS.KEY] =
          formatCurrency(actual);
        row[BUDGET_CSV_EXPORT_CONSTANTS.REPORT_SECTION_VARIANCE.KEY] =
          formatCurrency(allocation - actual);

        row[BUDGET_CSV_EXPORT_CONSTANTS.REPORT_FISCAL_YEAR_ALLOCATED.KEY] =
          formatCurrency(fiscalAmount);
        row[BUDGET_CSV_EXPORT_CONSTANTS.REPORT_FISCAL_YEAR_ACTUALS.KEY] =
          formatCurrency(fiscalActual);
        row[BUDGET_CSV_EXPORT_CONSTANTS.REPORT_FISCAL_YEAR_VARIANCE.KEY] =
          formatCurrency(fiscalAmount - fiscalActual);

        row[BUDGET_CSV_EXPORT_CONSTANTS.NOTES.KEY] = lineItem.note;

        if (
          !(
            lineItem.description === 'Cost of Labor' &&
            fiscalAmount === 0 &&
            fiscalActual === 0
          )
        ) {
          data.push(row);
        }
      });

      const childData = generateCsvDataFromParent(
        parent.children ?? [],
        fiscalYearStart,
        timeFrame
      );
      data.push(...childData);
    });

    return data;
  }

  return (
    <div className='pb-6'>
      <CSVLink
        data={csvData}
        headers={csvHeaders}
        filename={exportFileName}
        ref={csvLinkRef}
        hidden={true}
      >
        Export
      </CSVLink>
      <BudgetReportTableHeader
        appliedFilters={savedFilters}
        saveFilters={handleSaveFiltersSettings}
        toggleFilterComponent={toggleFilterComponent}
        exportCsvData={exportCsvData}
      />
      <div
        className={classnames('w-full rounded-b', {
          'overflow-auto border border-neutral-lighter-two ': true,
          'max-h-no-filtered-budget-table-body': isEmpty(savedFilters),
          'max-h-filtered-budget-table-body': !isEmpty(savedFilters),
        })}
      >
        <Table
          numberOfHeadRows={2}
          canSelectRows={false}
          className='relative w-full border-0 custom-table-spacing'
          data={{
            total: parentsHavingLineItems.length,
            headData: [
              {
                stickyHeader: true,
                headCells: [
                  {
                    content: '',
                    className: classnames('h-10 w-40 left-0 z-2'),
                  },
                  {
                    content: selectedTime.label,
                    className: 'text-center',
                    colSpan: 3,
                  },
                  selectedTime.value !== ActualTimes.FISCAL_CURRENT
                    ? {
                        content: (
                          <div>
                            {intl.get(
                              'BUDGET_PAGE.REPORT.TABLE.HEAD.FISCAL_YEAR'
                            )}{' '}
                            {fiscalYear}
                          </div>
                        ),
                        className: 'text-center',
                        colSpan: 3,
                      }
                    : null,
                  {
                    content: intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.DETAILS'),
                    className: 'text-center',
                    colSpan:
                      selectedBudgetView?.value !==
                      EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT
                        ? 5
                        : 4,
                  },
                ].filter(Boolean),
              },
              {
                stickyHeader: true,
                headCells: [
                  {
                    content: (
                      <div className='p-2'>
                        {selectedBudgetView?.value ===
                        EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT
                          ? intl.get('BUDGET_PAGE.EDIT.TABLE.HEAD.PROGRAM')
                          : selectedBudgetView?.value ===
                            EDIT_BUDGET_TABS.VENDORS
                          ? intl.get('BUDGET_PAGE.EDIT.TABLE.HEAD.VENDOR')
                          : intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.TEAM')}
                      </div>
                    ),
                    className: classnames('w-200px p-0 left-0 z-2'),
                  },
                  {
                    content: (
                      <div className='w-157px p-2'>
                        {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.ALLOCATED')}
                      </div>
                    ),
                    className: classnames('w-157px p-0 text-right'),
                  },
                  {
                    content: (
                      <div className='w-157px p-2'>
                        {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.ACTUAL')}
                      </div>
                    ),
                    className: classnames('w-157px p-0 text-right'),
                  },
                  {
                    content: (
                      <div className='w-157px p-2'>
                        {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.VARIANCE')}
                      </div>
                    ),
                    className: classnames('w-157px p-0 text-right'),
                  },
                  selectedTime.value !== ActualTimes.FISCAL_CURRENT
                    ? {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get(
                              'BUDGET_PAGE.REPORT.TABLE.HEAD.ALLOCATED'
                            )}
                          </div>
                        ),
                        className: classnames('w-157px p-0 text-right'),
                      }
                    : null,
                  selectedTime.value !== ActualTimes.FISCAL_CURRENT
                    ? {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.ACTUAL')}
                          </div>
                        ),
                        className: classnames('w-157px p-0 text-right'),
                      }
                    : null,
                  selectedTime.value !== ActualTimes.FISCAL_CURRENT
                    ? {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.VARIANCE')}
                          </div>
                        ),
                        className: classnames('w-157px p-0 text-right'),
                      }
                    : null,

                  selectedBudgetView?.value !== EDIT_BUDGET_TABS.TEAMS
                    ? {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.TEAM')}
                          </div>
                        ),
                        className: classnames('w-157px p-0'),
                      }
                    : null,
                  selectedBudgetView?.value !==
                  EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT
                    ? {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.PROGRAM')}
                          </div>
                        ),
                        className: classnames('w-157px p-0'),
                      }
                    : null,
                  selectedBudgetView?.value !==
                  EDIT_BUDGET_TABS.PROGRAM_OR_PROJECT
                    ? {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.PROJECT')}
                          </div>
                        ),
                        className: classnames('w-157px p-0'),
                      }
                    : null,
                  {
                    content: (
                      <div className='w-157px p-2'>
                        {intl.get(
                          'BUDGET_PAGE.REPORT.TABLE.HEAD.BUDGET_SOURCE'
                        )}
                      </div>
                    ),
                    className: classnames('w-157px p-0'),
                  },
                  selectedBudgetView?.value !== EDIT_BUDGET_TABS.VENDORS
                    ? {
                        content: (
                          <div className='w-157px p-2'>
                            {intl.get('BUDGET_PAGE.REPORT.TABLE.HEAD.VENDOR')}
                          </div>
                        ),
                        className: classnames('w-157px p-0'),
                      }
                    : null,

                  {
                    content: <div className='w-120px'></div>,
                    className: classnames('w-120px'),
                  },
                ].filter(Boolean),
              },
            ],
            rows: generateMultipleReportRows(
              parentsHavingLineItems,
              0,
              expandedRows,
              toggleExpandedRow,
              focusedRows,
              toggleFocusedRow,
              checkedRows,
              onEditLineItem,
              programOptions,
              projectOptions,
              vendorOptions,
              learningTeamOptions,
              selectedBudgetView?.value,
              fiscalMonth,
              monthIndecesForYearToDate(fiscalYear, fiscalMonth),
              selectedTime,
              sumOfAmounts,
              sumOfActuals,
              selectedBudget?.timeFrame ?? TimeFrame.QUARTERLY,
              focusedCellId,
              setFocusedCellId,
              false,
              true,
              handleAddNote,
              handleAddAttachment,
              handleDeleteLineItem
            ).flat(),
          }}
        />
      </div>

      {isNotificationVisible && snackbarProps && (
        <SnackbarNotification {...snackbarProps} />
      )}

      <DeleteLineItemModal
        isOpen={displayDeleteModal}
        closeModal={() => setDisplayDeleteModal(false)}
        onCancel={() => setDisplayDeleteModal(false)}
        title={selectedBudgetInfo?.description ?? ''}
        onDeleteProgram={() => setItemToDelete()}
      />

      <AttachmentsSidePanel
        open={displayAttachmentModal}
        lineItemId={storedLineItem}
        onClose={() => {
          setDisplayAttachmentModal(false);
        }}
      />

      <NoteSidePanel
        open={shouldDisplayNoteSidePanel}
        onClose={toggleNoteSidePanel}
        onNoteSaved={onNoteSaved}
        title={storedNoteData?.description ?? ''}
        note={storedNoteData?.note ?? ''}
      />
    </div>
  );
};

export default BudgetReportTable;
